// Copyright 2026 Bitech.id. Licensed under the MIT License.

import { resolveTarget } from '../config/config.js';
import { EventBuffer } from '../event-buffer.js';
import { buildEnvelope, buildLogPayload, buildPulsePayload } from '../envelope/envelope.js';
import { generateEventId, generateSpanId, generateTraceId } from '../ids.js';
import { Scope } from '../scope/scope.js';
import { exceptionFromError } from '../stacktrace/stacktrace.js';
import { Transport } from '../transport/transport.js';
import type {
  Breadcrumb,
  BiwaveEvent,
  BiwaveTarget,
  Level,
  LogEvent,
  LogLevel,
  Mechanism,
  Platform,
  PulseEvent,
  PulseKind,
  PulseStatus,
  RequestInfo,
  SdkInfo,
  TraceContext,
  UserInfo,
} from '../types.js';

export { generateTraceId, generateSpanId } from '../ids.js';

export interface BiwaveClientOptions {
  clientId: string;
  endpoint?: string;
  platform: Platform;
  sdk: SdkInfo;
  environment?: string;
  release?: string;
  serverName?: string;
  dist?: string;
  maxBreadcrumbs?: number;
  modules?: Record<string, string>;
  beforeSend?: (event: BiwaveEvent) => BiwaveEvent | null;
  getTraceContext?: () => TraceContext | undefined;
  getExtraBreadcrumbs?: () => Breadcrumb[];
  logBatchSize?: number;
  logFlushInterval?: number;
  logMaxMemoryEvents?: number;
  pulseBatchSize?: number;
  pulseFlushInterval?: number;
  pulseMaxMemoryEvents?: number;
}

/** Returned by startSpan() — call finish() once the measured work completes. */
export interface PulseSpan {
  finish(status?: PulseStatus): void;
}

export class BiwaveClient {
  readonly target: BiwaveTarget;
  readonly scope: Scope;
  private readonly transport: Transport;
  private readonly logBuffer: EventBuffer<LogEvent>;
  private readonly pulseBuffer: EventBuffer<PulseEvent>;
  /** Resolved once here so every event type (exception/log/pulse) always carries the same value — never undefined. */
  private readonly environment: string;
  /** Cached trace_id for the current request — set once per request via getTraceContext or auto-generated. */
  private currentTraceId: string | undefined;

  constructor(private readonly options: BiwaveClientOptions) {
    this.target = resolveTarget({ clientId: options.clientId, endpoint: options.endpoint });
    this.environment = options.environment ?? 'production';
    this.scope = new Scope(options.maxBreadcrumbs);
    this.transport = new Transport(this.target);
    this.logBuffer = new EventBuffer<LogEvent>({
      maxSize: options.logBatchSize,
      flushInterval: options.logFlushInterval,
      maxMemoryEvents: options.logMaxMemoryEvents,
      label: 'log',
      onFlush: (batch) => this.sendLogBatch(batch),
    });
    this.pulseBuffer = new EventBuffer<PulseEvent>({
      maxSize: options.pulseBatchSize,
      flushInterval: options.pulseFlushInterval,
      maxMemoryEvents: options.pulseMaxMemoryEvents,
      label: 'pulse',
      onFlush: (batch) => this.sendPulseBatch(batch),
    });
  }

  captureException(
    error: unknown,
    level: Level = 'error',
    request?: RequestInfo,
    mechanism?: Mechanism,
  ): string {
    const event = this.buildEvent(error, level, request, mechanism);
    return this.dispatch(event);
  }

  addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'> & { timestamp?: string }): void {
    this.scope.addBreadcrumb(breadcrumb);
  }

  setTag(key: string, value: string): void {
    this.scope.setTag(key, value);
  }

  setUser(user: UserInfo | undefined): void {
    this.scope.setUser(user);
  }

  setContext(key: string, context: Record<string, unknown>): void {
    this.scope.setContext(key, context);
  }

  captureLog(level: LogLevel, message: string, meta?: Record<string, unknown>): void {
    const event: LogEvent = {
      level,
      message,
      meta,
      occurred_at: new Date().toISOString(),
      time_ms: Date.now(),
      trace_id: this.options.getTraceContext?.()?.trace_id,
      environment: this.environment,
    };

    // warn/error logs are usually diagnosing something urgent — don't make
    // them wait for the next batch interval.
    if (level === 'error' || level === 'warn') {
      this.logBuffer.addAndFlush(event);
    } else {
      this.logBuffer.add(event);
    }
  }

  debug(message: string, meta?: Record<string, unknown>): void {
    this.captureLog('debug', message, meta);
  }

  info(message: string, meta?: Record<string, unknown>): void {
    this.captureLog('info', message, meta);
  }

  warn(message: string, meta?: Record<string, unknown>): void {
    this.captureLog('warn', message, meta);
  }

  error(message: string, meta?: Record<string, unknown>): void {
    this.captureLog('error', message, meta);
  }

  /**
   * Starts a Biwave Pulse span — call finish() once the measured work
   * completes. Prefer withSpan() when the work is a single function call;
   * use this directly when start/finish don't naturally sit in the same
   * callback (e.g. spanning a callback-based API).
   */
  startSpan(
    kind: PulseKind,
    name: string,
    method?: string,
    tags?: Record<string, unknown>,
  ): PulseSpan {
    const startTime = Date.now();
    const traceContext = this.options.getTraceContext?.();
    const traceId = traceContext?.trace_id;
    const parentSpanId = traceContext?.span_id;
    const spanId = generateSpanId();
    let finished = false;

    return {
      finish: (status: PulseStatus = 'ok') => {
        if (finished) return;
        finished = true;

        const event: PulseEvent = {
          kind,
          name,
          method,
          status,
          time_ms: startTime,
          duration_ms: Date.now() - startTime,
          trace_id: traceId,
          span_id: spanId,
          parent_span_id: parentSpanId,
          tags,
          environment: this.environment,
        };

        if (status === 'error') {
          this.pulseBuffer.addAndFlush(event);
        } else {
          this.pulseBuffer.add(event);
        }
      },
    };
  }

  /**
   * Wraps a function call in a Biwave Pulse span, measuring its duration and
   * recording status 'error' (then rethrowing) if it throws/rejects. Works
   * for both sync and async functions.
   */
  withSpan<T>(
    kind: PulseKind,
    name: string,
    fn: () => Promise<T>,
    method?: string,
    tags?: Record<string, unknown>,
  ): Promise<T>;
  withSpan<T>(
    kind: PulseKind,
    name: string,
    fn: () => T,
    method?: string,
    tags?: Record<string, unknown>,
  ): T;
  withSpan<T>(
    kind: PulseKind,
    name: string,
    fn: () => T | Promise<T>,
    method?: string,
    tags?: Record<string, unknown>,
  ): T | Promise<T> {
    const span = this.startSpan(kind, name, method, tags);

    let result: T | Promise<T>;
    try {
      result = fn();
    } catch (err) {
      span.finish('error');
      throw err;
    }

    if (result instanceof Promise) {
      return result.then(
        (value) => {
          span.finish('ok');
          return value;
        },
        (err: unknown) => {
          span.finish('error');
          throw err;
        },
      );
    }

    span.finish('ok');
    return result;
  }

  /** Best-effort: flushes any buffered logs/pulses immediately. Not guaranteed on a hard crash/SIGKILL. */
  flush(): Promise<void> {
    return Promise.all([this.logBuffer.flush(), this.pulseBuffer.flush()]).then(() => undefined);
  }

  /** Stops the log/pulse buffers' timers and performs a final flush — call on shutdown/unmount. */
  close(): void {
    this.logBuffer.destroy();
    this.pulseBuffer.destroy();
  }

  private async sendLogBatch(batch: LogEvent[]): Promise<void> {
    const body = buildLogPayload(batch, this.target);
    await this.transport.sendNow(body, this.target.ingestLogUrl, 'application/json');
  }

  private async sendPulseBatch(batch: PulseEvent[]): Promise<void> {
    const body = buildPulsePayload(batch, this.target);
    await this.transport.sendNow(body, this.target.ingestPulseUrl, 'application/json');
  }

  private buildEvent(
    error: unknown,
    level: Level,
    request?: RequestInfo,
    mechanism?: Mechanism,
  ): BiwaveEvent {
    const contexts = this.scope.getContexts();
    this.ensureTraceContext(contexts);

    const globalBreadcrumbs = this.scope.getBreadcrumbs();
    const extraBreadcrumbs = this.options.getExtraBreadcrumbs?.() ?? [];
    const breadcrumbs = [...extraBreadcrumbs, ...globalBreadcrumbs];

    const event: BiwaveEvent = {
      event_id: generateEventId(),
      timestamp: new Date().toISOString(),
      time_ms: Date.now(),
      platform: this.options.platform,
      level,
      environment: this.environment,
      release: this.options.release,
      dist: this.options.dist,
      server_name: this.options.serverName,
      exception: { values: [exceptionFromError(error, mechanism)] },
      breadcrumbs,
      request,
      user: this.scope.getUser(),
      tags: this.scope.getTags(),
      contexts,
      modules: this.options.modules,
      sdk: this.options.sdk,
    };

    return event;
  }

  private ensureTraceContext(contexts: Record<string, Record<string, unknown>>): void {
    const active = this.options.getTraceContext?.();
    const traceId = active?.trace_id ?? this.currentTraceId ?? generateTraceId();

    if (!active) {
      this.currentTraceId = traceId;
    }

    contexts.trace = {
      trace_id: traceId,
      span_id: generateSpanId(),
      op: 'default',
    };
  }

  private dispatch(event: BiwaveEvent): string {
    const finalEvent = this.options.beforeSend ? this.options.beforeSend(event) : event;
    if (!finalEvent) {
      return event.event_id;
    }

    const envelope = buildEnvelope(finalEvent, this.target);
    this.transport.send(envelope);
    return event.event_id;
  }
}
